"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown } from "lucide-react"
import { Search } from "lucide-react"

import { cn } from "../../lib/utils"
 
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "./select"

// Timeout in milliseconds for the typeahead feature
// Increasing this value gives users more time to type
const TYPEAHEAD_TIMEOUT = 3000; // 3 seconds (default is usually around 500ms)

interface EnhancedSelectProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export const EnhancedSelect = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  EnhancedSelectProps
>(({ children, onOpenChange, ...props }, ref) => {
  const [searchString, setSearchString] = React.useState("");
  const [searchInputValue, setSearchInputValue] = React.useState("");
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const isMobileRef = React.useRef<boolean>(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Handle keyboard events for typeahead
  const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
    // Only process if it's a single character key (not control keys, arrows, etc.)
    if (e.key.length === 1) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Append the new character to the search string
      setSearchString(prev => prev + e.key.toLowerCase());

      // Set a timeout to clear the search string after the specified delay
      timeoutRef.current = setTimeout(() => {
        setSearchString("");
      }, TYPEAHEAD_TIMEOUT);
    }
  }, []);


  // Focus the search input when the dropdown opens
  const focusSearchInput = React.useCallback(() => {
    // Use a small timeout to ensure the DOM is ready
    setTimeout(() => {
      if (searchInputRef.current) {
        // Only focus the search input on desktop devices
        if (!isMobileRef.current) {
          searchInputRef.current.focus();
        }
      }
    }, 50);
  }, []);

  // Function to focus the first item in the dropdown list
  const focusFirstItem = React.useCallback(() => {
    setTimeout(() => {
      if (contentRef.current) {
        // Find all option elements in the dropdown
        const options = contentRef.current.querySelectorAll('[role="option"]');
        
        // If there are any options, focus the first one
        if (options.length > 0) {
          const firstOption = options[0] as HTMLElement;
          firstOption.focus();
          
          // Optionally, add a visual indicator that this item is focused
          firstOption.setAttribute('data-highlighted', 'true');
          
          // Return true if we successfully focused an item
          return true;
        }
      }
      return false;
    }, 50);
  }, []);

  // Detect if the device is a mobile device
  React.useEffect(() => {
    // Simple mobile detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    isMobileRef.current = isMobile;
  }, []);

  // Add and remove event listeners when the select is opened/closed
  const handleOpenChange = React.useCallback((open: boolean) => {
    if (open) {
      // When opening, add the keydown listener
      document.addEventListener("keydown", handleKeyDown);
      
      // Focus the search input when opening
      focusSearchInput();
    } else {
      // When closing, remove the listener and clear any timeout
      document.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setSearchString("");
      setSearchInputValue("");
    }
    
    // Call the original onOpenChange if provided
    if (onOpenChange) {
      onOpenChange(open);
    }
  }, [handleKeyDown, onOpenChange, focusSearchInput, focusFirstItem]);

  // Clean up event listeners when component unmounts
  React.useEffect(() => {
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown]);

  // Function to filter items based on search input
  const filterItems = (children: React.ReactNode, searchValue: string): React.ReactNode => {
    if (!searchValue.trim()) {
      // If no search value, return all children
      return children;
    }

    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) {
        return child;
      }

      // Handle SelectItem elements
      if (child.type === SelectItem || 
          (typeof child.type === 'function' && (child.type as any).name === 'SelectItem') ||
          (child.props && 'value' in child.props)) {
        
        // Get the text content from children or value prop
        let itemText = "";
        if (typeof child.props.children === 'string') {
          itemText = child.props.children.toLowerCase();
        } else if (child.props.value) {
          itemText = child.props.value.toString().toLowerCase();
        }
        if (itemText && itemText.includes(searchValue.toLowerCase())) {
          return child;
        }
        return null;
      }

      // Handle SelectGroup elements
      if (child.type === SelectGroup) {
        const filteredChildren = filterItems(child.props.children, searchValue);
        
        // If all children are filtered out, don't render the group
        if (React.Children.count(filteredChildren) === 0) {
          return null;
        }
        
        return React.cloneElement(child, {
          ...child.props,
          children: filteredChildren
        });
      }

      // Pass through other elements
      if (child.props && child.props.children) {
        const filteredChildren = filterItems(child.props.children, searchValue);
        return React.cloneElement(child, {
          ...child.props,
          children: filteredChildren
        });
      }

      return child;
    });
  };

  // Create a ref for the content element and add search input
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.type === SelectContent) {
      // Filter the children of SelectContent
      const filteredChildren = filterItems(child.props.children, searchInputValue);
      
      // Add search input at the top of the dropdown
      return React.cloneElement(child, {
        ref: contentRef,
        ...child.props,
        children: (
          <>
            <div className="px-2 py-2 sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-50">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <input 
                  ref={searchInputRef}
                  className="w-full pl-8 pr-2 py-1 text-sm border rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400"
                  placeholder="Search..."
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  // Prevent events from bubbling up to avoid closing the dropdown
                  onClick={(e) => isMobileRef.current ? null : e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    
                    // Prevent Escape from closing dropdown
                    if (e.key === 'Escape') {
                      e.preventDefault();
                    }
                    
                    // Handle Tab and Down Arrow to move focus to the first item
                    if (e.key === 'Tab' || e.key === 'ArrowDown') {
                      e.preventDefault(); // Prevent default Tab behavior
                      
                      // Focus the first item in the list
                      focusFirstItem();
                      
                      return false;
                    }
                  }}
                  // Add touch event handlers for mobile
                  onTouchStart={(e) => isMobileRef.current ? e.stopPropagation() : null}
                  onTouchMove={(e) => isMobileRef.current ? e.stopPropagation() : null}
                  onTouchEnd={(e) => isMobileRef.current ? e.stopPropagation() : null}
                />
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              {filteredChildren}
            </div>
          </>
        )
      });
    }
    return child;
  });

  return (
    <Select {...props} onOpenChange={handleOpenChange}>
      {enhancedChildren}
    </Select>
  );
});

EnhancedSelect.displayName = "EnhancedSelect";

export {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
};
